import { endpoints } from '@api'
import { ArrowLeftIcon, Refresh } from '@blueprinthq/joy'
import {
  Box,
  Button,
  DrawerFooter,
  Flex,
  IconButton,
  Text,
  VStack
} from '@chakra-ui/react'
import { AlertBox } from '@components'
import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  getSessionControllerGetProgressNotesForSessionQueryKey,
  useProgressNoteTemplateMemoryControllerFindByNoteType
} from '~/clinician-api'
import Memories from './memories'

const MemoryManagementDrawer = ({
  onClose,
  noteType,
  noteTypeLabel,
  sessionId
}: {
  onClose: () => void
  noteType: string
  noteTypeLabel?: string | undefined
  sessionId: string
}) => {
  const queryClient = useQueryClient()

  const { mutateAsync: generateNewNote, isLoading: isGeneratingNote } =
    useMutation(async () => {
      await endpoints.generateProgressNote.request({
        sessionId: sessionId,
        noteType: noteType
      })
      await queryClient.invalidateQueries(
        getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
      )
      onClose()
    })

  const { data: memories = [] } =
    useProgressNoteTemplateMemoryControllerFindByNoteType({
      noteType
    })

  return (
    <>
      <Box
        height="95px"
        backgroundColor="primary"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="medium"
      >
        <Flex w="100%" height="80px" align="center">
          <IconButton
            aria-label="Back"
            icon={<ArrowLeftIcon fill="white" />}
            variant="ghost"
            color="white"
            onClick={onClose}
            _focus={{ outline: 'none' }}
            _hover={{ bg: 'rgba(255,255,255,0.1)' }}
          />
          <Text flex="1" color="white" textAlign="center">
            Memory for {noteTypeLabel} notes
          </Text>
          <Flex w="40px" />
        </Flex>
      </Box>
      <VStack spacing="medium" align="stretch" px="small" h="100%">
        {memories.some(memory => memory.isInvalid) && (
          <Box>
            <AlertBox p="small" status="warning">
              <Text fontSize="14px">
                We found some instructions that might be specific to individual
                clients. Consider reviewing these to ensure your {noteTypeLabel}{' '}
                notes work well for every client you see.
              </Text>
            </AlertBox>
          </Box>
        )}
        <Memories noteType={noteType} noteTypeLabel={noteTypeLabel} p="0" />
      </VStack>
      <DrawerFooter
        width="100%"
        px="small"
        py={0}
        borderTop="1px solid"
        borderColor="pale_gray"
      >
        <Box w="100%">
          <Flex justify="space-between" width="100%" py="16px" gap="12px">
            <Button variant="outline" onClick={onClose} size="lg" flex={1}>
              Cancel
            </Button>
            <Button
              onClick={() => generateNewNote()}
              size="lg"
              flex={1}
              isLoading={isGeneratingNote}
              leftIcon={<Refresh fill="White" h="16px" w="16px" />}
            >
              Generate new note
            </Button>
          </Flex>
        </Box>
      </DrawerFooter>
    </>
  )
}

export default MemoryManagementDrawer
